import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import type { TransformationPairContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Rewrite a sentence into a target form. MCQ mode: pick the correct variant.
 *  Typed mode: type the answer (typo-tolerant grade in grading.ts). Mirrors iOS
 *  TransformationPairView. */
export function TransformationPairRenderer({ content, answer, setAnswer, revealed, correct }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as TransformationPairContent;
  const typed = c.render_mode === "typed";

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-ink">{c.source}</div>

      {typed ? (
        <div className="mt-4">
          <input
            type="text"
            value={(answer as string) ?? ""}
            disabled={revealed}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder={t("Type your answer…")}
            className={`w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${
              revealed
                ? correct
                  ? "border-emerald-400 bg-emerald-50"
                  : "border-red-400 bg-red-50"
                : "border-black/10 bg-white"
            }`}
          />
          {revealed && !correct && (
            <p className="mt-1.5 text-xs text-emerald-700">
              {t("Correct:")} <span className="font-semibold">{c.target_primary}</span>
            </p>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {(c.variants ?? []).map((v) => {
            const chosen = answer === v.text;
            let tone = "border-black/10 bg-white hover:border-brand-300";
            if (revealed) {
              if (v.is_correct) tone = "border-emerald-400 bg-emerald-50";
              else if (chosen) tone = "border-red-400 bg-red-50";
              else tone = "border-black/10 bg-white opacity-60";
            } else if (chosen) tone = "border-brand-500 bg-brand-50 ring-1 ring-brand-300";
            return (
              <button
                key={v.text}
                type="button"
                disabled={revealed}
                onClick={() => setAnswer(v.text)}
                className={cx("w-full rounded-xl border px-4 py-3 text-left text-sm transition disabled:cursor-default", tone)}
              >
                {v.text}
                {revealed && chosen && !v.is_correct && v.rationale && (
                  <span className="mt-1 block text-xs text-red-600">{v.rationale}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
